import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { BadgeCheck, MapPin, Star } from "lucide-react";
import { PublicNav } from "@/components/site/PublicNav";
import { Footer } from "@/components/site/Footer";
import { listPublicPartners } from "@/lib/services.functions";

const partnersQuery = queryOptions({ queryKey: ["public-partners"], queryFn: () => listPublicPartners() });

export const Route = createFileRoute("/partners")({
  loader: ({ context }) => context.queryClient.ensureQueryData(partnersQuery),
  head: () => ({
    meta: [
      { title: "Find a Partner — Shields" },
      {
        name: "description",
        content: "Browse verified local tradespeople and service partners, their services, areas and genuine reviews.",
      },
      { property: "og:title", content: "Find a Partner — Shields" },
      { property: "og:description", content: "Verified local partners with insurance and identity checks." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PartnersPage,
});

function PartnersPage() {
  const { data: partners } = useSuspenseQuery(partnersQuery);
  return (
    <div className="min-h-screen">
      <PublicNav />
      <main className="section-pad mx-auto max-w-6xl py-14">
        <h1 className="text-3xl font-bold sm:text-4xl">Find a Partner</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Every partner listed here has passed Shields verification: identity confirmed, insurance
          checked and in date, and experience reviewed by our team.
        </p>

        {partners.length === 0 ? (
          <div className="card-surface mt-10 p-10 text-center">
            <h2 className="font-display text-lg font-bold">Partners are joining now</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
              We're onboarding verified partners in your area. The fastest way to get quotes is to
              post your job — eligible partners will respond directly.
            </p>
            <Link to="/book" className="btn-hero mt-6">
              Request a job
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {partners.map((p) => (
              <Link
                key={p.user_id}
                to="/partners/$partnerId"
                params={{ partnerId: p.user_id }}
                className="card-surface block p-6 transition-colors hover:border-primary"
              >
                <div className="flex items-start justify-between gap-3">
                  <h2 className="font-display text-lg font-bold">{p.trading_name}</h2>
                  <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2.5 py-1 text-xs font-bold text-success">
                    <BadgeCheck className="h-3.5 w-3.5" aria-hidden /> Verified
                  </span>
                </div>
                {p.headline && <p className="mt-1 text-sm font-semibold">{p.headline}</p>}
                {p.intro && <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.intro}</p>}
                {p.service_areas && (
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" aria-hidden /> {p.service_areas}
                  </p>
                )}
                {p.rating_count > 0 && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold">
                    <Star className="h-3.5 w-3.5 text-warning" aria-hidden />
                    {Number(p.rating_avg).toFixed(1)} · {p.rating_count} verified reviews
                  </p>
                )}
                <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
                  Verified by Shields: identity, insurance and competence checked.
                </p>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
